import { API_URL, fetchWithHandling } from './api';

// ---------------------------------------------------------------------------
// Teams chats API
//
// The signed-in member's own Teams chats (1:1, group, meeting), channels,
// reactions, unread, new chats, a suggested reply, and Send.
// Every call uses the caller's own Teams connection. Responses are the
// AppResponse envelope; failures throw { code, message }.
// ---------------------------------------------------------------------------

const BASE = `${API_URL}/api/teams-dm`;

const auth = (accessToken) => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${accessToken}`,
});

const chatUrl = (chatId) => `${BASE}/chats/${encodeURIComponent(chatId)}`;

/** { data: [{ id, type, with, members, last_message, last_message_at, last_from_me, last_from, unread, waiting, web_url }] } */
export async function listChats(accessToken) {
  return fetchWithHandling(`${BASE}/chats`, {
    method: 'GET',
    headers: auth(accessToken),
  });
}

/**
 * One page of a chat, oldest first: { data: { messages: [{ id, from, from_me,
 * text, html, images, files, reactions, edited, sent_at }], next } }.
 * Pass `next` back as cursor for the page before it.
 */
export async function listChatMessages(accessToken, chatId, cursor) {
  const query = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
  return fetchWithHandling(`${chatUrl(chatId)}/messages${query}`, {
    method: 'GET',
    headers: auth(accessToken),
  });
}

/** Reads the chat, in Teams too. */
export async function markChatRead(accessToken, chatId) {
  return fetchWithHandling(`${chatUrl(chatId)}/read`, {
    method: 'POST',
    headers: auth(accessToken),
  });
}

/** reaction: like | heart | laugh | surprised | sad | angry */
export async function reactToMessage(
  accessToken,
  chatId,
  messageId,
  reaction,
  on,
) {
  return fetchWithHandling(
    `${chatUrl(chatId)}/messages/${encodeURIComponent(messageId)}/reactions`,
    {
      method: 'POST',
      headers: auth(accessToken),
      body: JSON.stringify({ reaction, on }),
    },
  );
}

/** An inline image as an object URL (the request carries the member's token). */
export async function chatImageUrl(accessToken, chatId, messageId, hostedId) {
  const response = await fetch(
    `${chatUrl(chatId)}/messages/${encodeURIComponent(messageId)}/images/${encodeURIComponent(hostedId)}`,
    {
      headers: { Authorization: `Bearer ${accessToken}` },
      credentials: 'include',
    },
  );
  if (!response.ok) throw new Error('image');
  return URL.createObjectURL(await response.blob());
}

/** { data: [{ name, email }] } */
export async function searchPeople(accessToken, query) {
  return fetchWithHandling(`${BASE}/people?q=${encodeURIComponent(query)}`, {
    method: 'GET',
    headers: auth(accessToken),
  });
}

/**
 * One email: a 1:1 (reused if it exists). Several: a new group chat, named
 * by `topic` if given. Sends the first message. { data: { chat_id, ... } }
 */
export async function startChat(accessToken, emails, text, topic) {
  return fetchWithHandling(`${BASE}/chats`, {
    method: 'POST',
    headers: auth(accessToken),
    body: JSON.stringify({
      emails: Array.isArray(emails) ? emails : [emails],
      text,
      topic: topic || null,
    }),
  });
}

/** Edits one of the caller's own messages. */
export async function editChatMessage(accessToken, chatId, messageId, text) {
  return fetchWithHandling(
    `${chatUrl(chatId)}/messages/${encodeURIComponent(messageId)}`,
    {
      method: 'PATCH',
      headers: auth(accessToken),
      body: JSON.stringify({ text }),
    },
  );
}

/** Deletes one of the caller's own messages (undoable in Teams). */
export async function deleteChatMessage(accessToken, chatId, messageId) {
  return fetchWithHandling(
    `${chatUrl(chatId)}/messages/${encodeURIComponent(messageId)}`,
    { method: 'DELETE', headers: auth(accessToken) },
  );
}

/**
 * An image (inline) or another file (through the caller's OneDrive, needs
 * SharePoint connected with My OneDrive read and edit), with optional text.
 */
export async function sendChatAttachment(accessToken, chatId, file, text) {
  const form = new FormData();
  form.append('file', file);
  form.append('text', text || '');
  return fetchWithHandling(`${chatUrl(chatId)}/attachments`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
    body: form,
  });
}

/** { data: [{ id, name, channels: [{ id, name }] }] } */
export async function listChannels(accessToken) {
  return fetchWithHandling(`${BASE}/channels`, {
    method: 'GET',
    headers: auth(accessToken),
  });
}

const channelUrl = (teamId, channelId) =>
  `${BASE}/teams/${encodeURIComponent(teamId)}/channels/${encodeURIComponent(channelId)}/messages`;

/** Newest posts, oldest first, each with `replies`. */
export async function listChannelMessages(accessToken, teamId, channelId) {
  return fetchWithHandling(channelUrl(teamId, channelId), {
    method: 'GET',
    headers: auth(accessToken),
  });
}

/** A new post, or a reply when replyTo is a post id. */
export async function postToChannel(
  accessToken,
  teamId,
  channelId,
  text,
  replyTo,
) {
  return fetchWithHandling(channelUrl(teamId, channelId), {
    method: 'POST',
    headers: auth(accessToken),
    body: JSON.stringify({ text, reply_to: replyTo || null }),
  });
}

/** { data: { text, for_message_id, reason } }. Empty text: nothing to offer. */
export async function suggestChatReply(accessToken, chatId) {
  return fetchWithHandling(`${chatUrl(chatId)}/suggestion`, {
    method: 'POST',
    headers: auth(accessToken),
  });
}

/** Sends the reply into the chat as the caller. { data: { sent, id, sent_at } } */
export async function sendChatReply(accessToken, chatId, text) {
  return fetchWithHandling(`${chatUrl(chatId)}/messages`, {
    method: 'POST',
    headers: auth(accessToken),
    body: JSON.stringify({ text }),
  });
}
